import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { useState, useEffect } from "react";
import axiosInstance from "../utils/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import '../styles/PaymentForm.scss';

export default function PaymentForm({ bookingId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Auto-cancel after 4 minutes 40 seconds if user abandons payment
  useEffect(() => {
    const timeout = setTimeout(() => {
      handleCancel();
    }, 280000); // 4:40 minutes

    return () => clearTimeout(timeout);
  }, []);

  const handleCancel = async () => {
    try {
      await axiosInstance.post("/booking/cancel-booking/", {
        booking_id: bookingId,
      });

      const stored = sessionStorage.getItem("bookingContext");
      if (stored) {
        const parsed = JSON.parse(stored);
        navigate("/book/seat", { state: parsed.state });
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error("Error cancelling booking:", error);
      toast.error("Failed to cancel booking");
    }
  };

  const handlePay = async () => {
  if (!stripe || !elements) return;

  setLoading(true);
  const loadingToast = toast.loading("Processing payment...");

  try {
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
      redirect: "if_required", // Important!
    });

    // If there's an error
    if (error) {
      toast.update(loadingToast, {
        render: error.message || "Payment failed",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      setLoading(false);
      return;
    }

    // SUCCESS CASES

    // Case 1: Card payment - succeeds immediately
    if (paymentIntent && paymentIntent.status === "succeeded") {
      await finalizeBooking(paymentIntent.id, loadingToast);
      return;
    }

    // Case 2: Klarna/Amazon Pay - redirects, success handled on /payment-success
    // No action needed here — user will be redirected

  } catch (err) {
    console.error("Unexpected error:", err);
    toast.update(loadingToast, {
      render: "Something went wrong. Please try again.",
      type: "error",
      isLoading: false,
      autoClose: 5000,
    });
  }

  setLoading(false);
};

// Helper function to finalize booking
const finalizeBooking = async (paymentIntentId, loadingToast) => {
  try {
    const response = await axiosInstance.post("/booking/confirm-payment", {
      booking_id: bookingId,
      payment_intent_id: paymentIntentId,
    });

    toast.update(loadingToast, {
      render: "Payment successful!",
      type: "success",
      isLoading: false,
      autoClose: 1000,
    });

    setTimeout(() => {
      navigate("/booking-success", { state: response.data });
    }, 800);
  } catch (error) {
    toast.update(loadingToast, {
      render: "Payment succeeded but booking failed. Contact support.",
      type: "error",
      isLoading: false,
      autoClose: 5000,
    });
  }
};

  return (
    <div className="container">
      <PaymentElement />

      <div className="mt-4 d-flex gap-3 justify-content-center">
        <button
          disabled={!stripe || loading}
          onClick={handlePay}
          className="btn btn-success px-5 py-2"
        >
          {loading ? "Processing..." : "Pay Now"}
        </button>

        <button
          onClick={handleCancel}
          className="btn btn-outline-danger px-5 py-2"
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}